import { useCallback, useEffect, useMemo, useState } from 'react';

const normalizeValue = (value: unknown): unknown => {
  if (value instanceof Date) {
    return value.toISOString();
  }

  if (typeof value === 'string') {
    return value.trim();
  }

  if (Array.isArray(value)) {
    return value.map(normalizeValue);
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, normalizeValue(item)]),
    );
  }

  return value;
};

export function useDirtyState<T>(value: T, isReady = true, resetKey?: unknown) {
  const serializedValue = useMemo(
    () => JSON.stringify(normalizeValue(value)),
    [value],
  );

  const [initialValue, setInitialValue] = useState<string | null>(null);
  const [currentResetKey, setCurrentResetKey] = useState(resetKey);

  useEffect(() => {
    if (!isReady) {
      setInitialValue(null);
      return;
    }

    if (currentResetKey !== resetKey) {
      setCurrentResetKey(resetKey);
      setInitialValue(serializedValue);
      return;
    }

    if (initialValue === null) {
      setInitialValue(serializedValue);
    }
  }, [isReady, resetKey, currentResetKey, serializedValue, initialValue]);

  const isDirty =
    isReady && initialValue !== null && serializedValue !== initialValue;

  const resetDirty = useCallback(() => {
    setInitialValue(serializedValue);
  }, [serializedValue]);

  return { isDirty, resetDirty };
}
